import { useMemo, useState } from "react";
import axios from "axios";
import { differenceInCalendarDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { CHALES, DECORACOES, PET_FEE, brl } from "../data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const maskCPF = (v) =>
    v.replace(/\D/g, "").slice(0, 11)
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 11);
    if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
    return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

export default function Booking({ chale, setChale, decoracao, setDecoracao }) {
    const [range, setRange] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [form, setForm] = useState({
        nome: "", nascimento: "", cpf: "", email: "", telefone: "",
        acompanhante: "", hospedes: "2", pet: "nao",
        pagamento: "pix", parcelas: "1", observacoes: "",
    });

    const chaleData = CHALES.find((c) => c.id === chale);
    const decorData = DECORACOES.find((d) => d.id === decoracao);
    const noites = useMemo(
        () => (range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0),
        [range]
    );
    const subtotal = noites * chaleData.diaria;
    const petFee = form.pet === "sim" ? PET_FEE : 0;
    const total = subtotal + petFee + decorData.valor;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!range?.from || !range?.to || noites < 1) {
            toast.error("Escolha as datas de check-in e check-out no calendário.");
            return;
        }
        if (!form.nome || !form.nascimento || !form.cpf || !form.email || !form.telefone || !form.acompanhante) {
            toast.error("Preencha todos os campos obrigatórios.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/reservations`, {
                chale,
                checkin: format(range.from, "yyyy-MM-dd"),
                checkout: format(range.to, "yyyy-MM-dd"),
                nome: form.nome,
                nascimento: form.nascimento,
                cpf: form.cpf,
                email: form.email,
                telefone: form.telefone,
                acompanhante: form.acompanhante,
                hospedes: Number(form.hospedes),
                pet: form.pet === "sim",
                pagamento: form.pagamento,
                parcelas: form.pagamento === "cartao" ? Number(form.parcelas) : null,
                decoracao,
                observacoes: form.observacoes,
            });
            setSuccess({ total, noites });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Não foi possível enviar a reserva. Tente novamente.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "bg-white border-stone-300 rounded-none h-12 focus-visible:ring-emerald-800";

    return (
        <section id="reserva" data-testid="booking-section" className="py-24 md:py-32 bg-stone-100">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <Reveal>
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-800 mb-4">Reserva</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight max-w-2xl">
                        Escolha as datas e garanta seu refúgio.
                    </h2>
                    <p className="mt-4 text-stone-600 text-base sm:text-lg max-w-2xl">
                        Todas as datas a partir de hoje estão disponíveis. Sem pagamento online — confirmamos tudo pelo WhatsApp.
                    </p>
                </Reveal>

                <form onSubmit={handleSubmit} className="mt-16 grid lg:grid-cols-12 gap-12 items-start">
                    {/* Calendar + summary */}
                    <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-8">
                        <Reveal>
                            <div className="grid grid-cols-2 gap-2">
                                {CHALES.map((c) => (
                                    <button
                                        type="button"
                                        key={c.id}
                                        data-testid={`booking-chale-${c.id}`}
                                        onClick={() => setChale(c.id)}
                                        className={`p-4 text-left border transition-colors duration-300 ${
                                            chale === c.id
                                                ? "bg-emerald-900 border-emerald-900 text-stone-50"
                                                : "bg-stone-50 border-stone-300 text-stone-800 hover:border-emerald-800"
                                        }`}
                                    >
                                        <span className="block text-sm font-bold">{c.nome}</span>
                                        <span className={`block text-xs mt-1 ${chale === c.id ? "text-emerald-200" : "text-stone-500"}`}>
                                            {brl(c.diaria)} / diária
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </Reveal>

                        <Reveal delay={0.05}>
                            <div className="bg-stone-50 border border-stone-300 p-5">
                                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-emerald-800 mb-4">
                                    <CalendarDays size={14} /> 1. Escolha check-in e check-out
                                </p>
                                <div data-testid="booking-calendar" className="flex justify-center">
                                    <Calendar
                                        mode="range"
                                        selected={range}
                                        onSelect={setRange}
                                        disabled={{ before: new Date() }}
                                        locale={ptBR}
                                        numberOfMonths={1}
                                    />
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <div className="bg-emerald-950 text-stone-100 p-6 md:p-8" data-testid="booking-summary">
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-300">Resumo</p>
                                <div className="mt-5 space-y-3 text-sm">
                                    <div className="flex justify-between gap-4">
                                        <span className="text-stone-400">Chalé</span>
                                        <span className="font-bold text-right">{chaleData.nome}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-stone-400">Datas</span>
                                        <span className="font-bold text-right" data-testid="summary-dates">
                                            {noites > 0
                                                ? `${format(range.from, "dd/MM")} → ${format(range.to, "dd/MM")}`
                                                : "Selecione no calendário"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-stone-400">Diárias</span>
                                        <span className="font-bold" data-testid="summary-nights">
                                            {noites} × {brl(chaleData.diaria)}
                                        </span>
                                    </div>
                                    {decorData.valor > 0 && (
                                        <div className="flex justify-between gap-4">
                                            <span className="text-stone-400">Decoração {decorData.nome}</span>
                                            <span className="font-bold">{brl(decorData.valor)}</span>
                                        </div>
                                    )}
                                    {petFee > 0 && (
                                        <div className="flex justify-between gap-4">
                                            <span className="text-stone-400">Taxa pet (higienização)</span>
                                            <span className="font-bold">{brl(petFee)}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-6 pt-5 border-t border-emerald-800 flex items-end justify-between">
                                    <span className="text-xs uppercase tracking-widest text-stone-400">Total</span>
                                    <span className="text-3xl md:text-4xl font-black tracking-tight" data-testid="summary-total">
                                        {brl(total)}
                                    </span>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* Form */}
                    <Reveal delay={0.05} className="lg:col-span-7">
                        <div className="bg-stone-50 border border-stone-300 p-6 md:p-10 space-y-8">
                            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-800">
                                2. Seus dados
                            </p>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="nome">Nome completo *</Label>
                                    <Input id="nome" data-testid="form-nome" className={inputCls} value={form.nome} onChange={set("nome")} placeholder="Seu nome completo" required />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nascimento">Data de nascimento *</Label>
                                    <Input id="nascimento" data-testid="form-nascimento" type="date" className={inputCls} value={form.nascimento} onChange={set("nascimento")} required />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cpf">CPF *</Label>
                                    <Input id="cpf" data-testid="form-cpf" className={inputCls} value={form.cpf} onChange={(e) => setForm((f) => ({ ...f, cpf: maskCPF(e.target.value) }))} placeholder="000.000.000-00" required />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">E-mail *</Label>
                                    <Input id="email" data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="voce@email.com" required />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="telefone">Telefone (WhatsApp) *</Label>
                                    <Input id="telefone" data-testid="form-telefone" className={inputCls} value={form.telefone} onChange={(e) => setForm((f) => ({ ...f, telefone: maskPhone(e.target.value) }))} placeholder="(31) 90000-0000" required />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="acompanhante">Nome do acompanhante *</Label>
                                    <Input id="acompanhante" data-testid="form-acompanhante" className={inputCls} value={form.acompanhante} onChange={set("acompanhante")} placeholder="Nome completo do acompanhante" required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Número de hóspedes *</Label>
                                    <Select value={form.hospedes} onValueChange={set("hospedes")}>
                                        <SelectTrigger data-testid="form-hospedes" className={inputCls}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                                                <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "hóspede" : "hóspedes"}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <Label>Leva pet? *</Label>
                                    <RadioGroup value={form.pet} onValueChange={set("pet")} className="flex gap-3" data-testid="form-pet">
                                        {[["nao", "Não"], ["sim", `Sim (+ ${brl(PET_FEE)})`]].map(([v, l]) => (
                                            <Label key={v} className={`flex items-center gap-2 border px-4 py-3 cursor-pointer transition-colors ${form.pet === v ? "border-emerald-800 bg-emerald-50" : "border-stone-300"}`}>
                                                <RadioGroupItem value={v} data-testid={`form-pet-${v}`} />
                                                <span className="text-sm">{l}</span>
                                            </Label>
                                        ))}
                                    </RadioGroup>
                                </div>
                                <div className="space-y-2">
                                    <Label>Decoração romântica</Label>
                                    <Select value={decoracao} onValueChange={setDecoracao}>
                                        <SelectTrigger data-testid="form-decoracao" className={inputCls}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DECORACOES.map((d) => (
                                                <SelectItem key={d.id} value={d.id} data-testid={`form-decoracao-${d.id}`}>
                                                    {d.id === "nenhuma" ? "Não desejo" : `${d.nome} — ${brl(d.valor)}`}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Label>Forma de pagamento *</Label>
                                <RadioGroup value={form.pagamento} onValueChange={set("pagamento")} className="grid sm:grid-cols-2 gap-3" data-testid="form-pagamento">
                                    <Label className={`flex items-center gap-2 border px-4 py-4 cursor-pointer transition-colors ${form.pagamento === "pix" ? "border-emerald-800 bg-emerald-50" : "border-stone-300"}`}>
                                        <RadioGroupItem value="pix" data-testid="form-pagamento-pix" />
                                        <span className="text-sm font-bold">Pix</span>
                                    </Label>
                                    <Label className={`flex items-center gap-2 border px-4 py-4 cursor-pointer transition-colors ${form.pagamento === "cartao" ? "border-emerald-800 bg-emerald-50" : "border-stone-300"}`}>
                                        <RadioGroupItem value="cartao" data-testid="form-pagamento-cartao" />
                                        <span className="text-sm font-bold">Cartão de crédito</span>
                                    </Label>
                                </RadioGroup>
                                {form.pagamento === "cartao" && (
                                    <div className="space-y-2 pt-2">
                                        <Label>Parcelamento (sem juros)</Label>
                                        <Select value={form.parcelas} onValueChange={set("parcelas")}>
                                            <SelectTrigger data-testid="form-parcelas" className={inputCls}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                                    <SelectItem key={n} value={String(n)} data-testid={`form-parcelas-${n}`}>
                                                        {n}x de {brl(total / n)} sem juros
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="observacoes">Alguma solicitação especial?</Label>
                                <Textarea
                                    id="observacoes"
                                    data-testid="form-observacoes"
                                    className="bg-white border-stone-300 rounded-none min-h-[110px] focus-visible:ring-emerald-800"
                                    value={form.observacoes}
                                    onChange={set("observacoes")}
                                    placeholder="Aniversário, restrições alimentares, horário de chegada..."
                                />
                            </div>

                            <button
                                type="submit"
                                data-testid="booking-submit-button"
                                disabled={submitting}
                                className="w-full bg-emerald-900 text-stone-50 text-sm font-bold uppercase tracking-widest py-5 hover:bg-emerald-950 transition-colors disabled:opacity-60 flex items-center justify-center gap-3"
                            >
                                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                                {submitting ? "Enviando..." : `Concluir reserva — ${brl(total)}`}
                            </button>
                            <p className="text-xs text-stone-500 text-center">
                                Sem pagamento online. Você será direcionado ao WhatsApp para confirmar os dados.
                            </p>
                        </div>
                    </Reveal>
                </form>
            </div>

            <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
                <DialogContent className="rounded-none bg-stone-50 border-stone-300 sm:max-w-md" data-testid="success-dialog">
                    <DialogHeader>
                        <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-900 flex items-center justify-center">
                            <CheckCircle2 size={32} className="text-stone-50" />
                        </div>
                        <DialogTitle className="text-center text-2xl font-black tracking-tight text-stone-900" data-testid="success-message">
                            Sua reserva foi concluída com Sucesso!
                        </DialogTitle>
                        <DialogDescription className="text-center text-stone-600 pt-2">
                            {success && (
                                <>
                                    {success.noites} diária{success.noites > 1 ? "s" : ""} · Total de{" "}
                                    <strong>{brl(success.total)}</strong>.<br />
                                    Recebemos seus dados! Em breve entraremos em contato pelo
                                    WhatsApp informado para confirmar sua estadia.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <button
                        data-testid="success-close-button"
                        onClick={() => setSuccess(null)}
                        className="mt-2 w-full bg-emerald-900 text-stone-50 text-sm font-bold uppercase tracking-widest py-4 hover:bg-emerald-950 transition-colors"
                    >
                        Entendi, obrigado!
                    </button>
                </DialogContent>
            </Dialog>
        </section>
    );
}
